import { ClassReportSummary, OrganizationReportSummary } from 'models/IStats';

export type ReportSummary = ClassReportSummary | OrganizationReportSummary;

export interface GroupedReports<T extends ReportSummary> {
    lastWeekReports: T[];
    earlierReports: T[];
}

/**
 * Waits for report data to be available in the database before proceeding.
 * This prevents race conditions where navigation happens before MongoDB save is complete.
 * 
 * @param reportId - The ID of the report to check
 * @param httpClient - The HTTP client instance to use for requests
 * @param endpoint - API endpoint to check (e.g., 'reports' or 'organization-reports')
 * @param maxAttempts - Maximum number of attempts (default: 10)
 * @returns Promise that resolves when data is available or max attempts reached
 */
export const waitForReportData = async (reportId: string, httpClient: any, endpoint: string = '/reports', maxAttempts: number = 10): Promise<void> => {
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const reportData = await httpClient.get(`${endpoint}/${reportId}`);
            
            // Check if report data is available (MongoDB save is complete)
            // For class reports: check students_data exists (can be empty array for empty reports)
            // For organization reports: check class_reports exists
            if (reportData && (
                (reportData as any).students_data !== undefined || 
                (reportData as any).class_reports !== undefined
            )) {
                return;
            }
            
            // Exponential backoff with max delay of 3 seconds
            const delay = Math.min(500 * attempt, 3000);
            await new Promise(resolve => setTimeout(resolve, delay));
        } catch (error) {
            // On last attempt, proceed anyway to avoid infinite loops
            if (attempt === maxAttempts) {
                return;
            }
            
            // Wait before retry on error
            const delay = Math.min(500 * attempt, 3000);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
};

/**
 * Formats a date range for display
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Formatted date range string
 */
export function formatDateRange(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    
    const formatOptions: Intl.DateTimeFormatOptions = { 
        day: 'numeric', 
        month: 'short' 
    };
    
    // If years are different, include year in start date
    const startFormatOptions: Intl.DateTimeFormatOptions = startYear !== endYear 
        ? { ...formatOptions, year: 'numeric' }
        : formatOptions;
    
    const startFormatted = start.toLocaleDateString('en-US', startFormatOptions);
    const endFormatted = end.toLocaleDateString('en-US', formatOptions);
    
    return `${startFormatted} — ${endFormatted}, ${endYear}`;
}

/**
 * Groups reports by time into "last week" and "earlier" categories
 * @param reports - Array of reports to group
 * @returns Object with grouped reports
 */
export function groupReportsByTime<T extends ReportSummary>(reports: T[]): GroupedReports<T> {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const lastWeekReports = reports.filter(report => {
        const creationDate = new Date(report.creation_time);
        return creationDate >= oneWeekAgo;
    });

    const earlierReports = reports.filter(report => {
        const creationDate = new Date(report.creation_time);
        return creationDate < oneWeekAgo;
    });

    return {
        lastWeekReports,
        earlierReports
    };
}

/**
 * Formats date from US locale string to YYYY-MM-DD format for API
 * @param dateString - Date string in US locale format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateToYYYYMMDD(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Gets default date range (last 7 days)
 * @returns Object with start and end date strings in US locale format
 */
export function getDefaultDateRange(): { start: string; end: string } {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    return {
        start: startDate.toLocaleDateString("en-US"),
        end: endDate.toLocaleDateString("en-US")
    };
}